import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { TELECOM_NETWORK_CODE } from "../../utils/constants";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  createColumn,
  createEditAction,
  createViewAction,
  getForm,
  getRegister,
  formatDateTime,
} from "../../utils/helpers";
import { URL_TELECOM_NETWORK_CODES } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_TELECOM_NETWORK_CODES } from "../../utils/apis";

export const useTelecomNetworkCodes = () => {
  const tableColumns = () => [
    createColumn("code", true, "Telecom Code"),
    createColumn("network", true),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_TELECOM_NETWORK_CODES, item?.id),
              createEditAction(URL_TELECOM_NETWORK_CODES, item?.id),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    // Handle case where item is undefined or null
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Network Code Details",
          fields: {
            network_code: item?.code || "N/A",
            network_name: item?.network || "N/A",
            status: item?.deleted ? "Deleted" : "Active",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_by: item?.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`.trim() ||
                "System"
              : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by: item?.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`.trim() ||
                "Not updated"
              : "Not updated",
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_TELECOM_NETWORK_CODES);
  const activityFilter = "telecom_codes";

  return {
    tableData,
    tableColumns,
    viewData,
    formRoute,
    activityFilter,
  };
};

export const useTelecomNetworkCodeForm = ({
  id,
  updateFormValues,
}: TFormHookParams = {}) => {
  // Get existing telecom network codes data
  const { loadData, loading, data } = useGet();

  useEffect(() => {
    loadData({ api: API_TELECOM_NETWORK_CODES });
  }, []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        street_type: TELECOM_NETWORK_CODE,
      });
    }
  }, [updateFormValues]);

  // Check if this is an edit operation (id exists)
  const isEdit = !!id;

  const sections: IFormSection[] = [
    {
      title: "Telecom Network Code Details",
      fields: [
        {
          field_name: "code",
          label: "Network Code",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter network code (e.g., 703, 701, 704)",
            disabled: isEdit,
          },
        },
        {
          field_name: "network",
          label: "Network Name",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter network name (e.g., AIRTEL, MTN, AFRICELL)",
          },
        },
      ],
    },
  ];

  const title = "Telecom Network Code Management";
  const redirect = getRegister(URL_TELECOM_NETWORK_CODES);

  return {
    sections,
    title,
    redirect,
    loading,
    data,
  };
};

export const TelecomNetworkCodeForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { sections } = useTelecomNetworkCodeForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
